import {
  ComponentFaqFaq,
  ComponentTrainingVideoTrainingVideo,
  Enum_Componenttrainingvideotrainingvideo_Page,
  Maybe,
  useHelpQuery
} from 'generated/graphql'
import React from 'react'
import {
  VStack,
  Text,
  Center,
  Spinner,
  Box,
  HStack,
  Accordion,
  AccordionButton,
  AccordionItem,
  AccordionPanel,
  Icon
} from '@chakra-ui/react'
import { useLocation } from 'react-router-dom'

import { VscChevronDown, VscChevronRight } from 'react-icons/vsc'
import { CustomTabContainer } from 'layouts'
import VideoSlider from './VideoSlider'

type TrainingVideosProps = {
  videos: Maybe<ComponentTrainingVideoTrainingVideo>[]
  faqs: Maybe<ComponentFaqFaq>[]
  loading: boolean
}

const HelpVideos: React.FC<TrainingVideosProps> = ({ videos, faqs, loading }) => {
  // Both groupings are memoised so that they are referentially stable — they
  // are dependencies of the `tabPanels` memo below, and rebuilding them on
  // every render made that memo useless (it re-rendered the whole FAQ
  // accordion and the video slider on each parent render).
  const groupedVideos = React.useMemo(
    () =>
      videos.reduce((acc: any, video) => {
        const key = video?.category || ''
        if (!acc[key]) {
          acc[key] = []
        }
        acc[key].push(video)
        return acc
      }, {}),
    [videos]
  )

  const groupedFAQs = React.useMemo(
    () =>
      faqs.reduce((acc: any, faq) => {
        const key = faq?.category || ''
        if (!acc[key]) {
          acc[key] = []
        }
        acc[key].push(faq)
        return acc
      }, {}),
    [faqs]
  )

  const tabs = React.useMemo(
    () => [
      {
        title: 'Videos',
        bg: 'transparent'
      },
      {
        title: 'FAQs',
        bg: 'transparent'
      }
    ],
    []
  )

  const tabPanels = React.useMemo(
    () => [
      {
        component: <VideoSlider groupedVideos={groupedVideos} />
      },
      {
        component: (
          <VStack flex={1} spacing={6} w="100%" alignItems="flex-start">
            {Object.keys(groupedFAQs).map((category) => (
              <Box w="100%" key={category}>
                <HStack gap={2} mb={3}>
                  <Box w="12px" h="1px" bg="gray.900" />
                  <Text
                    fontSize="10px"
                    textTransform="uppercase"
                    letterSpacing="0.15em"
                    color="gray.400"
                    fontWeight="600"
                  >
                    {category.replace(/_/g, ' ')}
                  </Text>
                </HStack>
                <Accordion w="100%" allowToggle>
                  {groupedFAQs[category].map((faq: any) => (
                    <AccordionItem
                      border="none"
                      borderBottom="1px solid"
                      borderColor="gray.50"
                      w="100%"
                      key={faq?.id}
                    >
                      {({ isExpanded }) => (
                        <>
                          <h2>
                            <AccordionButton
                              px={0}
                              w="100%"
                              py={3}
                              borderRadius={0}
                              _hover={{ bg: 'transparent' }}
                            >
                              <Icon
                                as={isExpanded ? VscChevronDown : VscChevronRight}
                                boxSize={3}
                                color="gray.400"
                                mr={2}
                                flexShrink={0}
                              />
                              <Box flex="1" w="100%" textAlign="left">
                                <Text
                                  fontSize="xs"
                                  fontWeight={isExpanded ? '500' : '400'}
                                  color="gray.700"
                                  lineHeight="1.4"
                                >
                                  {faq?.question}
                                </Text>
                              </Box>
                            </AccordionButton>
                          </h2>
                          <AccordionPanel pb={3} pl={5} pr={0}>
                            <Text
                              color="gray.500"
                              fontSize="xs"
                              lineHeight="1.6"
                              textAlign="left"
                              w="100%"
                            >
                              {faq?.answer}
                            </Text>
                          </AccordionPanel>
                        </>
                      )}
                    </AccordionItem>
                  ))}
                </Accordion>
              </Box>
            ))}
          </VStack>
        )
      }
    ],
    [groupedVideos, groupedFAQs]
  )

  // The loading bail-out has to sit below every hook. It used to sit above the
  // two `useMemo` calls, so the very first render (while the help query was in
  // flight) ran no hooks and the next one ran two — React answers that with
  // "Rendered more hooks than during the previous render" and unmounts the
  // whole help panel.
  if (loading) {
    return (
      <Center py={8}>
        <Spinner color="gray.300" size="sm" />
      </Center>
    )
  }

  return <CustomTabContainer tabs={tabs} tabPanels={tabPanels} borderColor="gray.100" />
}

const HelpVideoGuides: React.FC<{}> = () => {
  const { data, loading } = useHelpQuery({
    fetchPolicy: 'cache-first'
  })
  const { pathname } = useLocation()
  const pageMap = [
    {
      pathname: ['/auth/communities'],
      page: Enum_Componenttrainingvideotrainingvideo_Page.GroupsGuide
    },
    {
      pathname: ['/auth/projects'],
      page: Enum_Componenttrainingvideotrainingvideo_Page.ProjectsHowTo
    },
    {
      pathname: ['/auth/task-types-list'],
      page: Enum_Componenttrainingvideotrainingvideo_Page.TaskBuilder_101
    },
    {
      pathname: ['/auth/tasks'],
      page: Enum_Componenttrainingvideotrainingvideo_Page.TasksFeed
    },
    {
      pathname: ['/auth/baoriders'],
      page: Enum_Componenttrainingvideotrainingvideo_Page.DataCollectors
    },
    {
      pathname: ['/auth/settings'],
      page: Enum_Componenttrainingvideotrainingvideo_Page.Settings
    },
    {
      pathname: ['/auth/onboarding'],
      page: Enum_Componenttrainingvideotrainingvideo_Page.Onboarding
    },
    {
      pathname: ['/auth/help'],
      page: Enum_Componenttrainingvideotrainingvideo_Page.Help
    }
  ]

  const videos = data?.trainingVideo?.data?.attributes?.training_videos || []
  const faqs = data?.trainingVideo?.data?.attributes?.FAQs || []

  const filteredVideoResults = videos.filter((video) => {
    const page = pageMap.find((page) => page.pathname.some((path) => pathname.includes(path)))
    return video?.page === page?.page
  })
  const filteredFAQResults = faqs.filter((faq) => {
    const page = pageMap.find((page) => page.pathname.some((path) => pathname.includes(path)))
    return faq?.page === page?.page
  })

  return <HelpVideos videos={filteredVideoResults} faqs={filteredFAQResults} loading={loading} />
}
export default HelpVideoGuides
